import { authorizedAxios } from "../../Actions/AxiosActions";
import { IDemReportRequest } from "../../Actions/ConfigurableReportActions";
import { IServiceResult } from "../../Models/Models";
import { IConfigurableReport, IConfigurableUserTemplate } from "../IConfigurableReport";

const getBaseUrl = (): string => {
    const hostname = window.location.hostname;
    switch (hostname) {
        case "platform.buxtonco.com":
            return "https://configurable-reporting.buxtonco.com";
        case "platform-qat.buxtonco.com":
            return "https://configurable-reporting-qat.buxtonco.com";
        case "platform-uat.buxtonco.com":
            return "https://configurable-reporting-uat.buxtonco.com";
        case "localhost":
            return "https://configurable-reporting-dev.buxtonco.com";
        default:
            throw new Error(`Unknown hostname: ${hostname}`);
    }
};

interface IConfigurableReportsAxios {
    GetConfigurableReports: (collaborate?: boolean) => Promise<IConfigurableReport[]>;
    DeleteConfigurableReport: (reportId: string) => Promise<boolean>;
    CreateConfigurableReport: (newReport: IDemReportRequest) => Promise<boolean>;
    CreateMultiConfigurableReport: (newReport: { reportName: string; combine: boolean; requestedAreas: IDemReportRequest[] }) => Promise<boolean>;
    GetUserTemplates: () => Promise<IConfigurableUserTemplate[]>;
}

export class ConfigurableReportsAxios implements IConfigurableReportsAxios {
    private ServiceToken: string;

    private async GetServiceToken(): Promise<string> {
        try {
            const response: IServiceResult<string> = await authorizedAxios("GET", "/token/servicetoken");
            if (response.result && response.data) {
                return response.data;
            } else {
                throw new Error("Bad response from service token endpoint");
            }
        } catch (err) {
            throw new Error(err.message);
        }
    }

    private async PerformRequest(url: string, options: RequestInit): Promise<Response> {
        let attempts = 0;
        const maxAttempts = 2;

        while (attempts < maxAttempts) {
            try {
                if (!this.ServiceToken) {
                    this.ServiceToken = await this.GetServiceToken();
                }
                options.headers = {
                    ...options.headers,
                    "Authorization": `Bearer ${this.ServiceToken}`,
                };

                const res = await fetch(url, options);

                // If a request using a service token fails with a 401, that means the token is expired.
                // Get a new token and try the request again (if the max attempts haven't been reached).
                if (res.status === 401 && attempts < maxAttempts - 1) {
                    this.ServiceToken = await this.GetServiceToken();
                    options.headers["Authorization"] = `Bearer ${this.ServiceToken}`;
                    attempts++;
                } else {
                    return res;
                }
            } catch (err) {
                console.error(`Error performing request: ${err.message}`);
                attempts++;
                if (attempts >= maxAttempts) {
                    throw err;
                }
            }
        }
    }

    public async GetConfigurableReports(collaborate?: boolean): Promise<IConfigurableReport[]> {
        const url = `${getBaseUrl()}/Report${collaborate ? "?getCidReports=true" : ""}`;
        const options = {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        };

        const res = await this.PerformRequest(url, options);
        if (res.ok) {
            const data: IConfigurableReport[] = await res.json();
            return data;
        } else {
            throw new Error(res.statusText);
        }
    }

    public async DeleteConfigurableReport(reportId: string): Promise<boolean> {
        const url = `${getBaseUrl()}/Report/${reportId}`;
        const options = {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
        };

        const res = await this.PerformRequest(url, options);
        return res.ok;
    }

    public async CreateConfigurableReport(newReport: IDemReportRequest): Promise<boolean> {
        const url = `${getBaseUrl()}/Report`;
        const options = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newReport),
        };

        const res = await this.PerformRequest(url, options);
        
        if (res.status !== 202) {
            return false;
        }

        return res.ok;
    }

    public async CreateMultiConfigurableReport(newReport: {
        reportName: string;
        combine: boolean;
        requestedAreas: IDemReportRequest[];
    }): Promise<boolean> {
        const url = `${getBaseUrl()}/Report/Combine`;
        const options = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newReport),
        };

        const res = await this.PerformRequest(url, options);

        if (res.status !== 202) {
            return false;
        }

        return res.ok;
    }

    public async GetUserTemplates(): Promise<IConfigurableUserTemplate[]> {
        const url = `${getBaseUrl()}/Template`;
        const options = {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        };

        const res = await this.PerformRequest(url, options);
        if (res.ok) {
            const data: IConfigurableUserTemplate[] = await res.json();

            return data;
        } else {
            throw new Error(res.statusText);
        }
    }
}
